import { useState } from "react";
import { useNavigate } from "react-router";
import BackLink from "../../../components/atoms/BackLink";
import Loader from "../../../components/atoms/Loader";
import KarteTemplateForm, {
  KarteTemplateFormType,
} from "../../../components/features/KarteTemplateForm";
import { UnprocessableEntityError } from "../../../errors";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import { KarteTemplateFormInterface } from "../../../interfaces/KarteTemplateInterface";
import SectionInterface from "../../../interfaces/SectionInterface";
import { useDeleteSectionKarteTemplate } from "./useDeleteSectionKarteTemplate";
import { useFetchSectionKarteTemplate } from "./useFetchSectionKarteTemplate";
import { useUpdateSectionKarteTemplate } from "./useUpdateSectionKarteTemplate";

interface Props {
  section: SectionInterface;
  karteTemplateId: string;
}

const SectionsSettingsKarteTemplatesEdit = ({
  section,
  karteTemplateId,
}: Props) => {
  const [apiErrors, setApiErrors] = useState<ApiErrorInterface[]>([]);
  const navigate = useNavigate();

  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const { data, isPending, error } = useFetchSectionKarteTemplate({
    sectionId: section.id,
    karteTemplateId,
  });
  useQueryError(error);

  const updateTemplateMutation = useUpdateSectionKarteTemplate({
    sectionId: section.id,
    karteTemplateId,
  });

  const deleteTemplateMutation = useDeleteSectionKarteTemplate({
    sectionId: section.id,
    karteTemplateId,
  });

  const handleUpdateTemplate = (values: KarteTemplateFormInterface) => {
    updateTemplateMutation.mutate(values, {
      onSuccess: () => {
        showSuccessMessage("テンプレートを更新しました");
        navigate(`/sections/${section.id}/settings/karte_templates`);
      },
      onError: (error) => {
        showErrorMessage("テンプレートの更新できませんでした");
        if (error instanceof UnprocessableEntityError) {
          setApiErrors(error.originalErrors);
        }
      },
    });
  };

  const handleDeleteTemplate = () => {
    deleteTemplateMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccessMessage("テンプレートを削除しました");
        navigate(`/sections/${section.id}/settings/karte_templates`);
      },
      onError: () => {
        showErrorMessage("テンプレートを削除できませんでした");
      },
    });
  };

  return (
    <>
      <BackLink>戻る</BackLink>
      {data && (
        <KarteTemplateForm
          type={KarteTemplateFormType.Edit}
          sectionId={section.id}
          karteTemplate={data}
          loading={isPending}
          onSubmit={handleUpdateTemplate}
          onDelete={handleDeleteTemplate}
          submitting={updateTemplateMutation.isPending}
          apiErrors={apiErrors}
        />
      )}
      <Loader loading={isPending} />
    </>
  );
};

export default SectionsSettingsKarteTemplatesEdit;
